import { API_ENDPOINTS } from '@/config/api.config';
import apiClient from './api';

// API Response wrapper
export interface ApiResponse<T> {
  success: boolean;
  code: number;
  description?: string;
  data?: T;
  error?: {
    error: string;
    error_description: string;
    code?: string;
    message?: string;
    details?: any;
  };
}

// Core interfaces based on USER-INVITATION-ENDPOINTS.md
export interface UserInvitationGroup {
  group_key: string;
  group_name: string;
  roles: UserInvitationRole[];
}

export interface UserInvitationRole {
  role_key: string;
  role_name: string;
}

export interface CreateInvitationRequest {
  email: string;
  full_name: string;
  given_name?: string;
  family_name?: string;
  mobile_number?: string;
  designation?: string;
  employee_id?: string;
  groups: UserInvitationGroup[];
  custom_metadata?: Record<string, any>;
  // Organizational Hierarchy
  reporting_manager_email?: string;
  bu_head_email?: string;
  hrbp_email?: string;
}

export interface InvitationConfig {
  email_template_id?: string;
  send_automatic_reminders?: boolean;
  reminder_frequency_days?: number;
  expiration_days: number;
}

export interface BulkInvitationRequest {
  invitations: CreateInvitationRequest[];
  invitation_config: InvitationConfig;
}

export interface UserInvitation {
  invitation_token: any;
  invitation_id: string;
  email: string;
  full_name: string;
  given_name?: string;
  family_name?: string;
  mobile_number?: string;
  designation?: string;
  employee_id?: string;
  groups: UserInvitationGroup[];
  custom_metadata?: Record<string, any>;
  status: 'pending' | 'accepted' | 'registered' | 'expired' | 'cancelled' | 'inactive';
  invited_by: string;
  invited_by_name: string;
  invited_at: string;
  expires_at: string;
  invitation_expires_at?: string; // Alternative field name from API
  send_automatic_reminders?: boolean;
  reminder_frequency_days?: number;
  reminder_count: number;
  last_reminder_sent_at?: string;
  accepted_at?: string;
  registered_at?: string;
  user_id?: string;
  registration_ip?: string;
}

export interface InvitationListParams {
  page?: number;
  limit?: number;
  status?: 'pending' | 'accepted' | 'registered' | 'expired' | 'cancelled' | 'inactive';
  search?: string;
  email?: string;
  group_key?: string;
  invited_by?: string;
  sort_by?: 'invited_at' | 'full_name' | 'status';
  sort_order?: 'asc' | 'desc';
}

export interface InvitationListResponse {
  pagination: any;
  invitations: UserInvitation[];
  total: number;
  page: number;
  limit: number;
  pages: number;
}

export interface UpdateInvitationRequest {
  full_name?: string;
  given_name?: string;
  family_name?: string;
  mobile_number?: string;
  designation?: string;
  employee_id?: string;
  groups?: UserInvitationGroup[];
  custom_metadata?: Record<string, any>;
  send_automatic_reminders?: boolean;
  reminder_frequency_days?: number;
  invitation_expires_in_days?: number;
}

export interface BulkResendRequest {
  invitation_ids: string[];
}

export interface BulkCancelRequest {
  invitation_ids: string[];
}

export interface BulkResendResponse {
  success: number;
  failed: number;
  errors: Array<{
    invitation_id: string;
    error: string;
  }>;
}

export interface BulkCancelResponse {
  success: number;
  failed: number;
  errors: Array<{
    invitation_id: string;
    error: string;
  }>;
}
export interface InvitationMetrics {
  total_invited: number;
  pending: number;
  accepted: number;
  registered: number;
  expired: number;
  cancelled: number;
  success_rate: number;
  average_time_to_register: number;
  invitations_by_month: Array<{
    month: string;
    count: number;
    registered: number;
  }>;
  top_inviters: Array<{
    user_id: string;
    user_name: string;
    invitation_count: number;
    success_count: number;
  }>;
}

// Email Template interfaces
export interface EmailTemplate {
  template_id: string;
  template_name: string;
  subject: string;
  html_content?: string;
  text_content?: string;
  template_type: 'invitation' | 'reminder' | 'welcome';
  is_default: boolean;
  is_active: boolean;
  placeholders?: string[];
  usage_count: number;
  last_used_at?: string;
  created_by?: string;
  created_by_name?: string;
  updated_by?: string;
  updated_by_name?: string;
  created_at: string;
  updated_at?: string;
}

export interface CreateTemplateRequest {
  template_name: string;
  subject: string;
  html_content: string;
  text_content: string;
  template_type: 'invitation' | 'reminder' | 'welcome';
  is_active: boolean;
  placeholders: string[];
}

export interface UpdateTemplateRequest {
  template_name?: string;
  subject?: string;
  html_content?: string;
  text_content?: string;
  is_active?: boolean;
}

export interface ValidateTemplateRequest {
  html_content: string;
  test_data: Record<string, string>;
}

export interface ValidateTemplateResponse {
  is_valid: boolean;
  rendered_html: string;
  missing_placeholders: string[];
  unused_placeholders: string[];
  errors: string[];
}

export interface TestEmailRequest {
  email: string;
}

// Public interfaces (no auth required)
export interface VerifyInvitationResponse {
  email: string;
  full_name: string;
  given_name?: string;
  family_name?: string;
  groups: Array<{
    group_key: string;
    group_name: string;
    roles: string[];
  }>;
  expires_at: string;
  company_name: string;
  invited_by_name: string;
  status: string;
}

export interface AcceptInvitationRequest {
  token: string;
}

export interface CompleteRegistrationRequest {
  token: string;
  password: string;
  security_question?: string;
  security_answer?: string;
  profile_picture?: string;
  additional_profile?: Record<string, any>;
}

export interface CompleteRegistrationResponse {
  user_id: string;
  email: string;
  full_name: string;
  groups: string[];
  message: string;
}

export interface InvitationStatusResponse {
  status: string;
  expires_at?: string;
  is_expired: boolean;
  days_remaining: number;
}

export class UserInvitationService {
  // Using centralized API endpoints from config

  /**
   * Helper function to extract error message from response
   */
  private getErrorMessage(error: any): string {
    // Check if it's an axios response error
    if (error?.response?.data) {
      const errorData = error.response.data;
      
      // Handle API error object format: { error: { error_description / message } }
      if (errorData.error && typeof errorData.error === 'object') {
        // New format: { error: { error_description: "..." } }
        if (errorData.error.error_description) {
          return errorData.error.error_description;
        }
        // Old format: { error: { message: "..." } }
        if (errorData.error.message) {
          return errorData.error.message;
        }
      }

      // Top-level error_description
      if (errorData.error_description) {
        return errorData.error_description;
      }

      // NestJS ValidationPipe format: `message` is a string OR an array of strings, each
      // possibly prefixed with the field path, e.g.
      // ["invitations.0.Disposable or temporary email addresses are not allowed"].
      // This carries the real reason, so prefer it over the generic `error` status text
      // ("Bad Request") which would otherwise mask it.
      if (errorData.message) {
        const normalised = Array.isArray(errorData.message)
          ? errorData.message
              .map((m: string) => String(m).replace(/^invitations\.\d+\./, '').trim())
              .join('; ')
          : errorData.message;
        if (normalised) {
          return normalised;
        }
      }

      // Generic string error code / HTTP status as a last resort (e.g. "user_exists", "Bad Request")
      if (typeof errorData.error === 'string' && errorData.error) {
        return errorData.error;
      }

      // Fallback to description
      if (errorData.description) {
        return errorData.description;
      }
    }
    
    // Fallback to error message
    return error?.message || 'An unexpected error occurred';
  }

  /**
   * Helper to handle API errors consistently
   */
  private handleApiError(error: any, defaultMessage: string): never {
    const message = this.getErrorMessage(error);
    console.error(defaultMessage, error);
    throw new Error(message);
  }

  // Protected endpoints (require JWT)
  
  /**
   * Create and send bulk user invitations
   */
  async createInvitations(request: BulkInvitationRequest): Promise<ApiResponse<{ invitations: UserInvitation[]; total: number; success?: number; failed?: number; errors?: Array<{ email: string; error: string }> }>> {
    try {
      const response = await apiClient.post<ApiResponse<{ invitations: UserInvitation[]; total: number; success?: number; failed?: number; errors?: Array<{ email: string; error: string }> }>>(
        API_ENDPOINTS.USER_INVITATIONS.CREATE, 
        request
      );
      return response.data;
    } catch (error: any) {
      this.handleApiError(error, 'Error creating invitations');
    }
  }

  /**
   * Get invitations with pagination and filtering
   */
  async getInvitations(params?: InvitationListParams): Promise<ApiResponse<InvitationListResponse>> {
    try {
      // Build payload - only include non-empty values
      const payload: Record<string, any> = {};

      if (params) {
        if (params.page !== undefined) payload.page = params.page;
        if (params.limit !== undefined) payload.limit = params.limit;
        if (params.status) payload.status = params.status;
        if (params.search) payload.search = params.search;
        if (params.email) payload.email = params.email;
        if (params.group_key) payload.group_key = params.group_key;
        if (params.invited_by) payload.invited_by = params.invited_by;
        if (params.sort_by) payload.sort_by = params.sort_by;
        if (params.sort_order) payload.sort_order = params.sort_order;
      }

      const response = await apiClient.post<ApiResponse<InvitationListResponse>>(
        API_ENDPOINTS.USER_INVITATIONS.LIST,
        payload
      );
      return response.data;
    } catch (error: any) {
      this.handleApiError(error, 'Error fetching invitations');
    }
  }

  /**
   * Get invitation by ID
   */
  async getInvitationById(invitationId: string): Promise<ApiResponse<UserInvitation>> {
    const response = await apiClient.get<ApiResponse<UserInvitation>>(
      API_ENDPOINTS.USER_INVITATIONS.GET_BY_ID(invitationId)
    );
    return response.data;
  }

  /**
   * Update invitation details
   */
  async updateInvitation(invitationId: string, request: UpdateInvitationRequest): Promise<ApiResponse<{ invitation_id: string; email: string; status: string }>> {
    const response = await apiClient.put<ApiResponse<{ invitation_id: string; email: string; status: string }>>(
      API_ENDPOINTS.USER_INVITATIONS.UPDATE(invitationId),
      request
    );
    return response.data;
  }

  /**
   * Cancel a pending invitation
   */
  async cancelInvitation(invitationId: string): Promise<ApiResponse<{ message: string }>> {
    try {
      const response = await apiClient.delete<ApiResponse<{ message: string }>>(
        API_ENDPOINTS.USER_INVITATIONS.DELETE(invitationId)
      );
      return response.data;
    } catch (error: any) {
      this.handleApiError(error, 'Error cancelling invitation');
    }
  }

  /**
   * Resend invitation email
   */
  async resendInvitation(invitationId: string): Promise<ApiResponse<{ invitation_id: string; email: string; last_reminder_sent_at: string }>> {
    try {
      const response = await apiClient.post<ApiResponse<{ invitation_id: string; email: string; last_reminder_sent_at: string }>>(
        API_ENDPOINTS.USER_INVITATIONS.RESEND(invitationId)
      );
      return response.data;
    } catch (error: any) {
      this.handleApiError(error, 'Error resending invitation');
    }
  }

  /**
   * Bulk resend invitations
   */
  async bulkResendInvitations(request: BulkResendRequest): Promise<ApiResponse<BulkResendResponse>> {
    try {
      const response = await apiClient.post<ApiResponse<BulkResendResponse>>(
        API_ENDPOINTS.USER_INVITATIONS.BULK_RESEND, 
        request
      );
      return response.data;
    } catch (error: any) {
      this.handleApiError(error, 'Error bulk resending invitations');
    }
  }

  /**
   * Bulk cancel invitations
   */
  async bulkCancelInvitations(request: BulkCancelRequest): Promise<ApiResponse<BulkCancelResponse>> {
    try {
      const response = await apiClient.post<ApiResponse<BulkResendResponse>>(
        API_ENDPOINTS.USER_INVITATIONS.BULK_CANCEL,
        request
      );
      return response.data;
    } catch (error: any) {
      this.handleApiError(error, 'Error bulk resending invitations');
    }
  }

  /**
   * Get invitations by status
   */
  async getInvitationsByStatus(status: string, params?: { page?: number; limit?: number }): Promise<ApiResponse<InvitationListResponse>> {
    const response = await apiClient.get<ApiResponse<InvitationListResponse>>(
      API_ENDPOINTS.USER_INVITATIONS.BY_STATUS(status),
      { params }
    );
    return response.data;
  }

  /**
   * Get invitations by group
   */
  async getInvitationsByGroup(groupKey: string, params?: { page?: number; limit?: number }): Promise<ApiResponse<InvitationListResponse & { group_key: string }>> {
    const response = await apiClient.get<ApiResponse<InvitationListResponse & { group_key: string }>>(
      API_ENDPOINTS.USER_INVITATIONS.BY_GROUP(groupKey),
      { params }
    );
    return response.data;
  }

  /**
   * Export invitations to CSV
   */
  async exportInvitationsCSV(params?: { status?: string; email?: string; group_key?: string; invited_by?: string }): Promise<Blob> {
    try {
      const response = await apiClient.get(API_ENDPOINTS.USER_INVITATIONS.EXPORT_CSV, {
        params,
        responseType: 'blob'
      });
      return response.data;
    } catch (error: any) {
      this.handleApiError(error, 'Error exporting invitations CSV');
    }
  }

  /**
   * Get invitation metrics and statistics
   */
  async getInvitationMetrics(): Promise<ApiResponse<InvitationMetrics>> {
    try {
      const response = await apiClient.get<ApiResponse<InvitationMetrics>>(
        API_ENDPOINTS.USER_INVITATIONS.METRICS
      );
      return response.data;
    } catch (error: any) {
      this.handleApiError(error, 'Error fetching invitation metrics');
    }
  }

  // Email Template Management

  /**
   * Initialize default templates
   */
  async initializeDefaultTemplates(): Promise<ApiResponse<{ message: string }>> {
    const response = await apiClient.post<ApiResponse<{ message: string }>>(
      API_ENDPOINTS.USER_INVITATIONS.TEMPLATES.INITIALIZE
    );
    return response.data;
  }

  /**
   * Create email template
   */
  async createEmailTemplate(request: CreateTemplateRequest): Promise<ApiResponse<{ template_id: string; template_name: string; template_type: string }>> {
    const response = await apiClient.post<ApiResponse<{ template_id: string; template_name: string; template_type: string }>>(
      API_ENDPOINTS.USER_INVITATIONS.TEMPLATES.CREATE,
      request
    );
    return response.data;
  }

  /**
   * Get email templates
   */
  async getEmailTemplates(templateType?: string): Promise<ApiResponse<EmailTemplate[]>> {
    const response = await apiClient.get<ApiResponse<EmailTemplate[]>>(
      API_ENDPOINTS.USER_INVITATIONS.TEMPLATES.LIST,
      { params: templateType ? { template_type: templateType } : undefined }
    );
    return response.data;
  }

  /**
   * Get template by ID
   */
  async getEmailTemplateById(templateId: string): Promise<ApiResponse<EmailTemplate>> {
    const response = await apiClient.get<ApiResponse<EmailTemplate>>(
      API_ENDPOINTS.USER_INVITATIONS.TEMPLATES.GET_BY_ID(templateId)
    );
    return response.data;
  }

  /**
   * Update email template
   */
  async updateEmailTemplate(templateId: string, request: UpdateTemplateRequest): Promise<ApiResponse<{ template_id: string; template_name: string }>> {
    const response = await apiClient.put<ApiResponse<{ template_id: string; template_name: string }>>(
      API_ENDPOINTS.USER_INVITATIONS.TEMPLATES.UPDATE(templateId),
      request
    );
    return response.data;
  }

  /**
   * Delete email template
   */
  async deleteEmailTemplate(templateId: string): Promise<ApiResponse<{ message: string }>> {
    const response = await apiClient.delete<ApiResponse<{ message: string }>>(
      API_ENDPOINTS.USER_INVITATIONS.TEMPLATES.DELETE(templateId)
    );
    return response.data;
  }

  /**
   * Clone email template
   */
  async cloneEmailTemplate(templateId: string, newName: string): Promise<ApiResponse<{ template_id: string; template_name: string }>> {
    const response = await apiClient.post<ApiResponse<{ template_id: string; template_name: string }>>(
      API_ENDPOINTS.USER_INVITATIONS.TEMPLATES.CLONE(templateId),
      { new_name: newName }
    );
    return response.data;
  }

  /**
   * Validate and preview email template
   */
  async validateEmailTemplate(request: ValidateTemplateRequest): Promise<ApiResponse<ValidateTemplateResponse>> {
    const response = await apiClient.post<ApiResponse<ValidateTemplateResponse>>(
      API_ENDPOINTS.USER_INVITATIONS.TEMPLATES.VALIDATE,
      request
    );
    return response.data;
  }

  /**
   * Send test email
   */
  async sendTestEmail(request: TestEmailRequest): Promise<ApiResponse<{ message: string }>> {
    const response = await apiClient.post<ApiResponse<{ message: string }>>(
      API_ENDPOINTS.USER_INVITATIONS.TEST_EMAIL,
      request
    );
    return response.data;
  }

  // Public endpoints (no auth required)

  /**
   * Verify invitation token (public)
   */
  async verifyInvitation(token: string): Promise<ApiResponse<VerifyInvitationResponse>> {
    const response = await apiClient.get<ApiResponse<VerifyInvitationResponse>>(
      API_ENDPOINTS.USER_INVITATIONS.PUBLIC.VERIFY,
      { params: { token } }
    );
    return response.data;
  }

  /**
   * Accept invitation (public)
   */
  async acceptInvitation(request: AcceptInvitationRequest): Promise<ApiResponse<{ email: string; status: string }>> {
    const response = await apiClient.post<ApiResponse<{ email: string; status: string }>>(
      API_ENDPOINTS.USER_INVITATIONS.PUBLIC.ACCEPT,
      request
    );
    return response.data;
  }

  /**
   * Complete user registration (public)
   */
  async completeRegistration(request: CompleteRegistrationRequest): Promise<ApiResponse<CompleteRegistrationResponse>> {
    const response = await apiClient.post<ApiResponse<CompleteRegistrationResponse>>(
      API_ENDPOINTS.USER_INVITATIONS.PUBLIC.REGISTER,
      request
    );
    return response.data;
  }

  /**
   * Check invitation status (public)
   */
  async checkInvitationStatus(token: string): Promise<ApiResponse<InvitationStatusResponse>> {
    const response = await apiClient.get<ApiResponse<InvitationStatusResponse>>(
      API_ENDPOINTS.USER_INVITATIONS.PUBLIC.STATUS,
      { params: { token } }
    );
    return response.data;
  }

  /**
   * Health check for public service
   */
  async healthCheck(): Promise<ApiResponse<{ message: string; timestamp: string }>> {
    const response = await apiClient.get<ApiResponse<{ message: string; timestamp: string }>>(
      API_ENDPOINTS.USER_INVITATIONS.PUBLIC.HEALTH
    );
    return response.data;
  }
}

export const userInvitationService = new UserInvitationService();